import { importCogoJsonAppInput, type ImportedCogoXyzApp } from './cogo-xyz-import'

type CogoElementRecord = Record<string, unknown>
type SourceAdapterInputType = 'html' | 'react' | 'markdown' | 'prompt'
type SourceAdapterMode =
  | 'dom-common-tags'
  | 'source-code-preview'
  | 'markdown-common-blocks'
  | 'prompt-intent-preview'

function safeId(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48) || 'source'
}

function readAttr(element: Element, name: string): string | undefined {
  const value = element.getAttribute(name)
  return value && value.trim() ? value : undefined
}

function classNameFor(element: Element): string | undefined {
  return readAttr(element, 'class') ?? readAttr(element, 'className')
}

function textElement(id: string, text: string, tag?: string): CogoElementRecord {
  return {
    id,
    type: 'text',
    text,
    ...(tag ? { tag } : {}),
  }
}

function sourceCodePreviewElement(id: string, source: string): CogoElementRecord {
  return {
    id,
    type: 'container',
    className: 'rounded-lg border bg-muted/30 p-4 font-mono text-sm whitespace-pre-wrap',
    children: [
      textElement(`${id}-source`, source.trim() || '(empty source)', 'pre'),
    ],
  }
}

function cleanMarkdownInline(value: string): string {
  return value
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/_([^_]+)_/g, '$1')
    .trim()
}

function markdownRootElement(source: string): CogoElementRecord {
  const lines = source.replace(/\r\n/g, '\n').split('\n')
  const children: CogoElementRecord[] = []
  let paragraphLines: string[] = []
  let codeLines: string[] = []
  let codeFenceOpen = false
  let codeBlockIndex = 0

  const flushParagraph = () => {
    const text = cleanMarkdownInline(paragraphLines.join(' '))
    paragraphLines = []
    if (!text) return
    children.push({
      ...textElement(`md-paragraph-${children.length}`, text),
      className: 'text-base leading-7',
    })
  }

  const flushCodeBlock = () => {
    children.push(sourceCodePreviewElement(`md-code-${codeBlockIndex}`, codeLines.join('\n')))
    codeLines = []
    codeBlockIndex += 1
  }

  lines.forEach((rawLine, index) => {
    const line = rawLine.trimEnd()
    const trimmed = line.trim()

    if (codeFenceOpen) {
      if (trimmed.startsWith('```')) {
        codeFenceOpen = false
        flushCodeBlock()
      } else {
        codeLines.push(rawLine)
      }
      return
    }

    if (trimmed.startsWith('```')) {
      flushParagraph()
      codeFenceOpen = true
      codeLines = []
      return
    }

    if (!trimmed) {
      flushParagraph()
      return
    }

    const heading = trimmed.match(/^(#{1,6})\s+(.+)$/)
    if (heading) {
      flushParagraph()
      children.push({
        ...textElement(`md-heading-${index}`, cleanMarkdownInline(heading[2] ?? ''), `h${heading[1]?.length ?? 1}`),
        className: 'font-semibold tracking-normal',
      })
      return
    }

    const quote = trimmed.match(/^>\s?(.*)$/)
    if (quote) {
      flushParagraph()
      children.push({
        ...textElement(`md-quote-${index}`, cleanMarkdownInline(quote[1] ?? ''), 'blockquote'),
        className: 'border-l-2 pl-4 text-muted-foreground',
      })
      return
    }

    const listItem = trimmed.match(/^([-*+]|\d+[.)])\s+(.+)$/)
    if (listItem) {
      flushParagraph()
      children.push({
        ...textElement(`md-list-${index}`, cleanMarkdownInline(listItem[2] ?? ''), 'li'),
        className: 'pl-5 text-base leading-7',
        marker: listItem[1],
      })
      return
    }

    paragraphLines.push(trimmed)
  })

  if (codeFenceOpen) flushCodeBlock()
  flushParagraph()

  return {
    id: 'source-root',
    type: 'container',
    className: 'min-h-screen w-full space-y-4 p-6',
    children: children.length > 0 ? children : [sourceCodePreviewElement('source-empty', source)],
  }
}

function promptRootElement(source: string): CogoElementRecord {
  return {
    id: 'source-root',
    type: 'container',
    className: 'min-h-screen w-full space-y-4 p-6',
    children: [
      {
        id: 'prompt-intent-panel',
        type: 'container',
        className: 'space-y-3 rounded-lg border bg-muted/30 p-4',
        children: [
          textElement('prompt-intent-title', 'App intent', 'h1'),
          {
            ...textElement('prompt-intent-body', source.trim() || '(empty prompt)', 'pre'),
            className: 'whitespace-pre-wrap text-sm leading-6',
          },
        ],
      },
    ],
  }
}

function convertHtmlElement(element: Element, path: string): CogoElementRecord | null {
  const tag = element.tagName.toLowerCase()
  if (tag === 'script' || tag === 'style' || tag === 'noscript') return null

  const id = readAttr(element, 'id') ?? `${path}-${tag}`
  const className = classNameFor(element)
  const childElements = Array.from(element.childNodes)
    .flatMap((child, index) => {
      if (child.nodeType === Node.TEXT_NODE) {
        const text = child.textContent?.replace(/\s+/g, ' ').trim()
        return text ? [textElement(`${id}-text-${index}`, text)] : []
      }
      if (child.nodeType === Node.ELEMENT_NODE) {
        const converted = convertHtmlElement(child as Element, `${id}-${index}`)
        return converted ? [converted] : []
      }
      return []
    })

  if (/^h[1-6]$/.test(tag)) {
    return {
      ...textElement(id, element.textContent?.trim() ?? '', tag),
      ...(className ? { className } : {}),
    }
  }

  if (tag === 'p' || tag === 'span' || tag === 'label' || tag === 'strong' || tag === 'em') {
    return {
      ...textElement(id, element.textContent?.trim() ?? '', tag === 'p' ? undefined : tag),
      ...(className ? { className } : {}),
    }
  }

  if (tag === 'button') {
    return {
      id,
      type: 'button',
      text: element.textContent?.trim() || readAttr(element, 'aria-label') || 'Button',
      ...(className ? { className } : {}),
    }
  }

  if (tag === 'input' || tag === 'textarea') {
    return {
      id,
      type: 'input',
      placeholder: readAttr(element, 'placeholder') ?? '',
      ...(readAttr(element, 'type') ? { inputType: readAttr(element, 'type') } : {}),
      ...(className ? { className } : {}),
    }
  }

  if (tag === 'img') {
    return {
      id,
      type: 'image',
      src: readAttr(element, 'src') ?? '',
      alt: readAttr(element, 'alt') ?? '',
      ...(className ? { className } : {}),
    }
  }

  if (tag === 'a') {
    return {
      id,
      type: 'button',
      text: element.textContent?.trim() || readAttr(element, 'href') || 'Link',
      href: readAttr(element, 'href'),
      variant: 'link',
      ...(className ? { className } : {}),
    }
  }

  return {
    id,
    type: 'container',
    ...(className ? { className } : {}),
    children: childElements.length > 0
      ? childElements
      : [textElement(`${id}-text`, element.textContent?.trim() ?? '')].filter((child) => child.text),
  }
}

function htmlRootElement(source: string): CogoElementRecord {
  if (typeof DOMParser === 'undefined') {
    return sourceCodePreviewElement('source-root', source)
  }
  const parsed = new DOMParser().parseFromString(source, 'text/html')
  const roots = Array.from(parsed.body.children)
    .map((element, index) => convertHtmlElement(element, `html-${index}`))
    .filter((element): element is CogoElementRecord => Boolean(element))
  return {
    id: 'source-root',
    type: 'container',
    className: 'min-h-screen w-full space-y-4 p-6',
    children: roots.length > 0 ? roots : [sourceCodePreviewElement('source-empty', source)],
  }
}

function sourceBundle(params: {
  title: string
  source: string
  sourceType: SourceAdapterInputType
  mode: SourceAdapterMode
  root: CogoElementRecord
}): Record<string, unknown> {
  const appId = safeId(params.title)
  return {
    schemaVersion: 'cogo-application/v1',
    manifest: {
      appId,
      name: params.title,
      pages: [{ id: 'home', route: '/', blocks: ['source-preview'] }],
    },
    blocks: {
      'source-preview': {
        meta: {
          id: 'source-preview',
          name: 'source-preview',
          sourceType: params.sourceType,
        },
        ui: {
          schemaVersion: '3.0',
          root: params.root,
        },
      },
    },
    metadata: {
      cogoXyzSourceAdapter: {
        sourceType: params.sourceType,
        mode: params.mode,
        sourceLength: params.source.length,
      },
    },
  }
}

function rootAndModeForInput(inputType: SourceAdapterInputType, input: string): {
  root: CogoElementRecord
  mode: SourceAdapterMode
} {
  if (inputType === 'html') {
    return { root: htmlRootElement(input), mode: 'dom-common-tags' }
  }
  if (inputType === 'markdown') {
    return { root: markdownRootElement(input), mode: 'markdown-common-blocks' }
  }
  if (inputType === 'prompt') {
    return { root: promptRootElement(input), mode: 'prompt-intent-preview' }
  }
  return { root: sourceCodePreviewElement('source-root', input), mode: 'source-code-preview' }
}

export async function importSourceAppInput(params: {
  title: string
  input: string
  inputType: SourceAdapterInputType
}): Promise<ImportedCogoXyzApp> {
  const { root, mode } = rootAndModeForInput(params.inputType, params.input)
  const bundle = sourceBundle({
    title: params.title,
    source: params.input,
    sourceType: params.inputType,
    mode,
    root,
  })
  return importCogoJsonAppInput({
    title: params.title,
    input: JSON.stringify(bundle),
  })
}
